/**
 * APPEN — det yttersta lagret. Bestämmer VILKEN VÄRLD användaren är i, aldrig
 * vilken skärm; det senare är navigeringens jobb.
 *
 * FYRA VÄRLDAR, i den här ordningen:
 *
 *   1. laddar          ME är på väg. Ingenting flimrar förbi.
 *   2. utloggad        Inloggningsskärmen, med SKÄLET om det fanns ett.
 *                      Inloggningen är en sexsiffrig kod som skrivs in i
 *                      appen (skarmar/LoggaIn.tsx) — ingen länksida längre.
 *   3. ingen_firma     Inloggad utan firma — ett riktigt läge, inte ett fel.
 *   4. inloggad        Appen. Först HÄR finns navigering och lägen.
 *
 * DEN GAMLA LÄNKSÖKVÄGEN '/logga-in' (till 2026-09-11 tog den emot
 * inloggningslänken) har ingen skärm längre, men ett gammalt brev i en inkorg
 * ska inte ge 404: adressen skrivs om till startsidan innan något ritas, och
 * fragmentet med den gamla tokenen försvinner med den. Sedan gäller
 * världarna ovan — en utloggad möter inloggningsskärmen och ber om en kod.
 *
 * NAVIGERINGEN LIGGER INNANFÖR SESSIONEN, med flit. Routern behöver veta
 * `kanSeLaget` för att veta vilka skärmar som finns, och det vet den först när
 * rollen är läst ur ME. Ligger den utanför skulle den behöva gissa en gång och
 * rätta sig sedan — och under den gissningen skulle lagets skärmar finnas för
 * alla.
 */

import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { glomUtloggningsfel } from './api';
import { GAMMAL_LANKSOKVAG } from './api/konstanter';
import { Navigeringsvard, useNavigering, usePlats } from './app/navigering';
import { Sessionsvard, useSession } from './app/session';
import { VYER } from './app/vyer';
import { LoggaIn } from './skarmar/LoggaIn';
import { NyFirma } from './skarmar/NyFirma';
import { Appram, Knapp, Laddar, Rubrik, Yta, Yttreram } from './ui';

// Den gamla länksökvägen -> startsidan, FÖRE första renderingen. replaceState
// och inte assign: ingen omladdning, ingen ny post i historiken, och
// fragmentet ('#token=...') följer inte med.
if (globalThis.location.pathname === GAMMAL_LANKSOKVAG) {
  globalThis.history.replaceState(null, '', '/');
}

/** Skärmen som platsen pekar ut. Bor innanför <Navigeringsvard>. */
const Aktuellskarm = () => {
  const { skarm } = usePlats();
  const Vy = VYER[skarm.id];
  return <Vy />;
};

/**
 * TOPPRADEN. Kugghjulet går till inställningarna — och det kan bara kopplas
 * INNANFÖR <Navigeringsvard>, för det är där `ga` finns. Därför den här lilla
 * komponenten i stället för en rad i <Inloggadvarld>: den senare RENDERAR
 * navigeringsvärden och står alltså utanför den.
 */
const Toppram = ({ namn, children }: { readonly namn: string; readonly children: ReactNode }) => {
  const { ga } = useNavigering();
  return (
    <Appram namn={namn} onInstallningar={() => ga('installningar')}>
      {children}
    </Appram>
  );
};

const Inloggadvarld = () => {
  const { session } = useSession();
  if (session.status !== 'inloggad') return null;
  return (
    <Navigeringsvard kanSeLaget={session.kanSeLaget}>
      <Toppram namn={session.konto.name}>
        <Aktuellskarm />
      </Toppram>
    </Navigeringsvard>
  );
};

/**
 * INGEN FIRMA — ett riktigt läge, inte ett fel, och numera inte heller en
 * återvändsgränd: formuläret som lägger upp firman står HÄR, i samma skärm.
 *
 * <NyFirma /> är samma komponent som skärmen 'nyfirma' inne i appen. Den står
 * utanför <Navigeringsvard> här och vet om det själv (useNavigeringOm), så det
 * enda den här världen behöver göra är att rendera den. Texten om ägarskapet,
 * provperioden och betalningsdagen bor numera i den, så att den syns på båda
 * ställena och bara finns skriven en gång.
 */
const IngenFirma = ({ namn }: { readonly namn: string }) => {
  const { loggaUt } = useSession();
  return (
    <Yttreram>
      <Rubrik
        titel={`Hej ${namn}`}
        under="Ditt konto finns, men du är inte med i någon firma än."
      />
      <Yta>
        <p style={{ margin: 0 }}>
          Antingen bjuder chefen in dig — då kommer du in så fort han lagt upp dig —
          eller så lägger du upp din egen firma här nedanför.
        </p>
      </Yta>
      <NyFirma />
      <Knapp variant="stor-tyst" onClick={loggaUt}>
        Logga ut
      </Knapp>
    </Yttreram>
  );
};

const Varldsvaljare = () => {
  const { session, laddaOm } = useSession();

  // Koden gick igenom och bevisen ligger i valvet: glöm skälet till förra
  // utloggningen och hämta ME, så att sessionen byter värld.
  const efterInloggning = useCallback(() => {
    glomUtloggningsfel();
    laddaOm();
  }, [laddaOm]);

  switch (session.status) {
    case 'laddar':
      return (
        <Yttreram>
          <Laddar rader={4} />
        </Yttreram>
      );
    case 'utloggad':
      return <LoggaIn skal={session.skal} vidInloggning={efterInloggning} />;
    case 'ingen_firma':
      return <IngenFirma namn={session.konto.name} />;
    case 'inloggad':
      return <Inloggadvarld />;
  }
};

export const App = () => (
  <Sessionsvard>
    <Varldsvaljare />
  </Sessionsvard>
);
